import { db } from '@/core/db/dexie'
import { sessionStore } from '@/core/auth/SessionStore'
import { uuidv7 } from '@/lib/uuidv7'
import type { OutFoto } from '@/core/db/entities'
import { pushFotoOutbox, type PushSummary } from '@/core/sync/fotoSync'
import { triggerForegroundSync } from '@/core/sync/foregroundSync'
import { requestBgSync } from '@/core/sync/registerBgSync'
import { photoStorage } from '@/core/storage'
import { saveToGallery } from '@/core/storage/galleryStorage'

/**
 * Grava uma foto (já com watermark queimado e EXIF GPS) no outbox e dispara push.
 */
export interface SaveFotoInput {
  projetoId: number
  projetoCodigo: string
  servicoExecutadoId: number | null
  servicoExecutadoNome: string | null
  encarregadoId: number | null
  encarregadoNome: string | null
  equipeId: number | null
  equipeNome: string | null
  lat: number | null
  lng: number | null
  accuracy: number | null
  fileBlob: Blob
  width: number | null
  height: number | null
  obs: string | null
}

export async function saveFoto(
  input: SaveFotoInput,
): Promise<{ id: string; pushResult: PushSummary | null }> {
  const user = sessionStore.current()
  if (!user) throw new Error('Sem sessão de usuário')

  const id = uuidv7()
  const capturedAt = new Date().toISOString()

  // PhotoStorage decide ONDE persistir o blob:
  //  - Web/PWA: Dexie indexedDB (handle.kind='blob' — armazenamos em fileBlob)
  //  - Capacitor Android: Filesystem nativo (handle.kind='path' — armazenamos
  //    em localPath; fileBlob fica null)
  const handle = await photoStorage().save(input.fileBlob, id)

  const entity: OutFoto = {
    id,
    controleProducaoId: null,
    carreteiroId: null,
    projetoId: input.projetoId,
    projetoCodigo: input.projetoCodigo,
    servicoExecutadoId: input.servicoExecutadoId,
    servicoExecutadoNome: input.servicoExecutadoNome,
    encarregadoId: input.encarregadoId,
    encarregadoNome: input.encarregadoNome,
    equipeId: input.equipeId,
    equipeNome: input.equipeNome,
    lat: input.lat,
    lng: input.lng,
    accuracy: input.accuracy,
    capturedAt,
    fileBlob: handle.kind === 'blob' ? handle.blob : null,
    localPath: handle.kind === 'path' ? handle.path : null,
    mime: input.fileBlob.type || 'image/jpeg',
    sizeBytes: input.fileBlob.size,
    width: input.width,
    height: input.height,
    sha256: null,
    obs: input.obs,
    cadastradorNome: user.userNome,
    syncStatus: 'PENDING',
  }

  await db.out_foto.add(entity)

  // Salva cópia na Galeria/Files (no-op em web). Não-bloqueante: se falhar,
  // o registro fica no outbox normalmente. Usuário pode achar fotos depois
  // no Files app em Pictures/InfraWork/<projeto>-<data>-<id>.jpg.
  void saveToGallery(input.fileBlob, {
    fotoId: id,
    projetoCodigo: input.projetoCodigo,
    capturedAt,
  })

  void requestBgSync('outbox-photos')

  let pushResult: PushSummary | null = null
  try {
    pushResult = await pushFotoOutbox()
  } catch (e) {
    console.warn('[fotoRepo] push falhou; mantendo no outbox:', e)
  }

  // Trigger global em paralelo (sem await) — dá feedback no SyncStatusIndicator
  // e descarrega outras filas (produção/carreteiro) se houver. ignoreCooldown
  // pra reagir imediatamente ao save mesmo se um ciclo recente tiver rodado.
  void triggerForegroundSync('post-save-foto', { ignoreCooldown: true })

  return { id, pushResult }
}

/**
 * Exclui uma foto do outbox local — libera blob/file no storage também.
 * Permitido apenas em status PENDING/FAILED (não interrompe SYNCING em andamento).
 */
export async function deleteFoto(id: string): Promise<void> {
  const foto = await db.out_foto.get(id)
  if (!foto) return
  if (foto.syncStatus === 'SYNCING') {
    throw new Error('Foto em sync, aguarde')
  }
  // Limpa blob/file primeiro — se falhar, ainda assim removemos o registro
  // (defesa contra arquivo órfão é melhor que registro órfão).
  try {
    if (foto.localPath) {
      await photoStorage().delete({
        kind: 'path',
        path: foto.localPath,
        mime: foto.mime,
        size: foto.sizeBytes,
      })
    } else if (foto.fileBlob) {
      await photoStorage().delete({ kind: 'blob', blob: foto.fileBlob })
    }
  } catch (e) {
    console.warn('[fotoRepo.deleteFoto] storage cleanup falhou:', e)
  }
  await db.out_foto.delete(id)
}
